// Content Status Alert - displays status messages for content submissions

import { cn } from "@repo/ui/lib/utils";
import { AlertTriangle, Check, Clock, Send, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";

// ============================================================================
// Helper
// ============================================================================

function formatDate(date: number | string | Date | null | undefined): string {
  if (!date) return "";

  let dateObj: Date;
  if (typeof date === "number") {
    // Heuristic: timestamps >= 1e12 are in milliseconds (dates after ~2001),
    // smaller values are in seconds and need to be converted
    const timestampMs = date >= 1e12 ? date : date * 1000;
    dateObj = new Date(timestampMs);
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) return "";

  return format(dateObj, "MMMM d, yyyy");
}

// ============================================================================
// Base Alert Component
// ============================================================================

interface AlertBoxProps {
  variant?: "default" | "success" | "warning" | "destructive";
  icon: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

function AlertBox({
  variant = "default",
  icon,
  title,
  description,
  children,
  className,
}: AlertBoxProps) {
  const variantStyles = {
    default: "border-border bg-muted/50",
    success: "border-success-foreground/20 bg-success/50",
    warning: "border-warning-foreground/20 bg-warning/50",
    destructive:
      "border-destructive/30 bg-destructive/10 dark:border-destructive dark:bg-destructive/20",
  };

  const titleStyles = {
    default: "text-foreground",
    success: "text-success-foreground",
    warning: "text-warning-foreground",
    destructive: "text-destructive-text",
  };

  const descriptionStyles = {
    default: "text-muted-foreground",
    success: "text-success-foreground",
    warning: "text-warning-foreground",
    destructive: "text-destructive-text",
  };

  return (
    <div
      className={cn(
        "relative w-full rounded-lg border p-4",
        variantStyles[variant],
        className,
      )}
      role="alert"
    >
      <div className="flex gap-3">
        <div className="shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0 space-y-1">
          {title && (
            <h5
              className={cn("font-medium leading-none", titleStyles[variant])}
            >
              {title}
            </h5>
          )}
          {description && (
            <div className={cn("text-sm", descriptionStyles[variant])}>
              {description}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Status Alerts
// ============================================================================

interface PendingApprovalAlertProps {
  /** Date content was uploaded/submitted */
  dateUploaded?: number | string | Date | null;
}

/**
 * Alert shown when content is pending brand approval.
 */
export function PendingApprovalAlert({
  dateUploaded,
}: PendingApprovalAlertProps) {
  const formattedDate = formatDate(dateUploaded);

  return (
    <AlertBox
      variant="warning"
      icon={<Clock className="h-4 w-4 text-warning-foreground" />}
      title={
        formattedDate ? `Submitted on ${formattedDate}` : "Content Submitted"
      }
      description="Pending Approval"
    />
  );
}

interface ApprovedAlertProps {
  /** Date content was submitted */
  submittedDate?: number | string | Date | null;
  /** Date content was approved by brand */
  approvedDate?: number | string | Date | null;
  /** Date content was completed (for no-approval flow) */
  completedDate?: number | string | Date | null;
}

/**
 * Alert shown when content has been approved.
 */
export function ApprovedAlert({
  submittedDate,
  approvedDate,
  completedDate,
}: ApprovedAlertProps) {
  const formattedSubmitted = formatDate(submittedDate);
  const formattedApproved = formatDate(approvedDate);
  const formattedCompleted = formatDate(completedDate);

  // If we have an approval date, show the full flow
  if (formattedApproved) {
    return (
      <AlertBox
        variant="success"
        icon={<Check className="h-4 w-4 text-success-foreground" />}
        title={
          formattedSubmitted ? `Submitted on ${formattedSubmitted}` : undefined
        }
        description={`Content Approved on ${formattedApproved}`}
      />
    );
  }

  // No approval required - just show completed
  return (
    <AlertBox
      variant="success"
      icon={<Check className="h-4 w-4 text-success-foreground" />}
      title={
        formattedCompleted
          ? `Completed on ${formattedCompleted}`
          : "Content Completed"
      }
    />
  );
}

interface AwaitingPostAlertProps {
  /** Date content was submitted for review */
  contentSubmissionDate?: number | string | Date | null;
  /** Date content was approved */
  contentApprovalDate?: number | string | Date | null;
}

/**
 * Alert shown when content is approved but waiting for live post.
 */
export function AwaitingPostAlert({
  contentSubmissionDate,
  contentApprovalDate,
}: AwaitingPostAlertProps) {
  const formattedSubmitted = formatDate(contentSubmissionDate);
  const formattedApproved = formatDate(contentApprovalDate);

  return (
    <AlertBox
      variant="success"
      icon={<Send className="h-4 w-4 text-success-foreground" />}
      title={
        formattedSubmitted ? `Submitted on ${formattedSubmitted}` : undefined
      }
      description={
        formattedApproved
          ? `Content Approved on ${formattedApproved}`
          : "Pending Approval"
      }
    />
  );
}

interface ChangesRequestedAlertProps {
  /** Brand's rejection comment/feedback */
  brandComment?: string | null;
  /** List of guidelines that need to be corrected */
  rejectedGuidelines?: string[];
}

/**
 * Alert shown when brand has requested changes to the content.
 */
export function ChangesRequestedAlert({
  brandComment,
  rejectedGuidelines,
}: ChangesRequestedAlertProps) {
  return (
    <AlertBox
      variant="destructive"
      icon={<AlertTriangle className="h-4 w-4 text-destructive-text" />}
      title="Changes Requested"
    >
      <div className="space-y-2 text-sm text-destructive-text">
        {rejectedGuidelines && rejectedGuidelines.length > 0 && (
          <div>
            <p className="font-medium">
              The brand requested that the following guidelines be corrected:
            </p>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {rejectedGuidelines.map((guideline, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key -- guidelines don't have unique IDs
                <li key={index}>{guideline}</li>
              ))}
            </ul>
          </div>
        )}
        {brandComment && (
          <div>
            <p className="font-medium">Comment:</p>
            <div
              className="mt-1"
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(brandComment),
              }}
            />
          </div>
        )}
      </div>
    </AlertBox>
  );
}

interface BrandCommentAlertProps {
  /** Comment from the brand */
  comment: string;
}

/**
 * Alert showing a brand comment on approved content.
 */
export function BrandCommentAlert({ comment }: BrandCommentAlertProps) {
  return (
    <AlertBox
      variant="default"
      icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
      title="Comment"
    >
      <div
        className="text-sm text-muted-foreground"
        // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment) }}
      />
    </AlertBox>
  );
}
